import React, { useState, useEffect } from 'react';
import { Save, CheckSquare, Archive, Loader } from 'lucide-react';
import api from '../utils/api';


const ArchiveRules = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    auto_archive_completed_tasks: false,
    auto_archive_delay_value: 7,
    auto_archive_delay_unit: 'days'
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/workplace-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setPreferences({
          auto_archive_completed_tasks: response.data.auto_archive_completed_tasks || false,
          auto_archive_delay_value: response.data.auto_archive_delay_value || 7,
          auto_archive_delay_unit: response.data.auto_archive_delay_unit || 'days'
        });
      }
    } catch (error) {
      console.error('Failed to fetch workplace preferences:', error);
      // If preferences don't exist yet, use defaults
      if (error.response?.status === 404) {
        setPreferences({
          auto_archive_completed_tasks: false,
          auto_archive_delay_value: 7,
          auto_archive_delay_unit: 'days'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const token = localStorage.getItem('access_token');
      await api.put(`/workplace-preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage({ 
        type: 'success', 
        text: 'Workplace preferences saved successfully!' 
      });
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Failed to save workplace preferences:', error);
      setMessage({ 
        type: 'error', 
        text: `Failed to save preferences: ${error.response?.data?.detail || error.message}` 
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <CheckSquare className="w-6 h-6 text-purple-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Archive Rules</h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure automatic archiving rules for completed tasks
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Auto Archive Setting */}
          <div className="border border-gray-200 rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <Archive className="w-8 h-8 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Auto-Archive Completed Tasks
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Automatically archive tasks when they are marked as complete. 
                  Archived tasks are hidden from active views but can still be accessed 
                  from the archive section.
                </p>
                
                <div className="flex items-center space-x-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.auto_archive_completed_tasks}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        auto_archive_completed_tasks: e.target.checked
                      })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                  <span className="text-sm font-medium text-gray-700">
                    {preferences.auto_archive_completed_tasks ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                {preferences.auto_archive_completed_tasks && (
                  <div className="mt-6 space-y-4">
                    {/* Delay Configuration */}
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Archive Delay Settings</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Choose how long to wait after a task is completed before automatically archiving it.
                        This gives your team time to review deliverables and ensure everything is finalized.
                      </p>
                      
                      <div className="flex items-center space-x-3">
                        <label className="text-sm font-medium text-gray-700">
                          Archive completed tasks after:
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="365"
                          value={preferences.auto_archive_delay_value}
                          onChange={(e) => setPreferences({
                            ...preferences,
                            auto_archive_delay_value: parseInt(e.target.value) || 1
                          })}
                          className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        <select
                          value={preferences.auto_archive_delay_unit}
                          onChange={(e) => setPreferences({
                            ...preferences,
                            auto_archive_delay_unit: e.target.value
                          })}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="days">Days</option>
                          <option value="weeks">Weeks</option>
                        </select>
                      </div>

                      <div className="mt-3 text-sm text-gray-600">
                        Preview: Tasks will be archived <strong className="text-purple-600">
                          {preferences.auto_archive_delay_value} {preferences.auto_archive_delay_unit}
                        </strong> after being marked as "Done"
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <CheckSquare className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">How it works:</p>
                          <ul className="list-disc list-inside space-y-1 text-blue-700">
                            <li>Tasks marked as "Done" will wait for the configured delay period</li>
                            <li>Gives team time to review deliverables and task completion</li>
                            <li>After the delay period, tasks are automatically archived</li>
                            <li>Applies to all projects and team members</li>
                            <li>Archived tasks remain searchable and accessible</li>
                            <li>You can manually archive or unarchive tasks at any time</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Success/Error Message */}
          {message.text && (
            <div className={`p-4 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Preferences</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchiveRules;
